/**
 * Свои автосообщения партнёра (#207) в мини-аппе кабинета: список и правка.
 *
 * Воронка «Путь клиента» живёт на сайте: это широкий экран с полосами и точками, на
 * телефоне он читался бы плохо. Здесь то, что нужно с телефона: создать, поправить,
 * выключить, удалить, и цифры каждого сообщения. Данные из того же `journey`, что у
 * сайта, второй ручки нет.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Info, Mail, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import type { Journey, RuleFilterKey, WarmupRule, WarmupRuleInput } from '@/api/types';
import { SkeletonLine } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { TelegramEditor } from '@/components/text-editor/TelegramEditor';
import { getWebApp, haptic } from '@/lib/telegram';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/warmup-rules/$botId')({ component: WarmupRules });

const FILTERS: ReadonlyArray<{ key: RuleFilterKey; label: string }> = [
  { key: 'live_sub', label: 'Есть живая подписка' },
  { key: 'took_trial', label: 'Брал пробник' },
  { key: 'paid', label: 'Платил хоть раз' },
];

const errText = (e: unknown, fallback: string): string =>
  (e instanceof ApiError
    ? (e.body as { error?: { message?: string } } | null)?.error?.message
    : undefined) || fallback;

type Target = { mode: 'new' } | { mode: 'edit'; rule: WarmupRule };

function WarmupRules() {
  const { botId } = Route.useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState<Target | null>(null);
  const q = useQuery({ queryKey: ['journey', botId, 30], queryFn: () => api.getJourney(botId, 30) });

  // Telegram BackButton: из редактора в список, из списка на карточку бота.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    const onBack = () =>
      target ? setTarget(null) : navigate({ to: '/bot' });
    tg.BackButton.show();
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate, botId, target]);

  if (target && q.data) {
    return <Editor botId={botId} journey={q.data} target={target} onDone={() => setTarget(null)} />;
  }

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-1 pt-2 flex items-center gap-3">
        <Link
          to="/bot"
          className="w-9 h-9 rounded-[12px] surface-inset flex items-center justify-center shrink-0 active:scale-95 transition-transform"
          aria-label="Назад"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </Link>
        <h2 className="font-display text-lg font-semibold tracking-tight">Свои автосообщения</h2>
      </div>

      <div className="flex gap-2.5 text-[12px] text-muted-foreground surface-inset rounded-[14px] p-3.5 leading-relaxed">
        <Info className="w-4 h-4 shrink-0 mt-px text-primary-light" />
        <div>
          Свой сценарий и срок. Уходит только тем, кто попал в условие после создания
          сообщения, и не чаще одного своего сообщения в сутки на человека. Воронка с
          цифрами по шагам на сайте, раздел «Путь клиента».
        </div>
      </div>

      {q.isPending ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="surface-inset rounded-[14px] p-3.5 space-y-2">
              <SkeletonLine className="w-40" />
              <SkeletonLine className="w-56" />
            </div>
          ))}
        </div>
      ) : q.isError || !q.data ? (
        <div className="surface-inset rounded-[14px] p-3.5 text-[13px] text-muted-foreground">
          Не удалось загрузить.{' '}
          <button type="button" className="text-primary-light underline" onClick={() => void q.refetch()}>
            Повторить
          </button>
        </div>
      ) : (
        <List journey={q.data} onOpen={setTarget} />
      )}
    </div>
  );
}

function List({ journey, onOpen }: { journey: Journey; onOpen: (t: Target) => void }) {
  const full = journey.rules.length >= journey.limits.max_rules;
  return (
    <div className="space-y-2">
      {journey.rules.length === 0 ? (
        <p className="px-1 text-[13px] text-muted-foreground">
          Своих сообщений пока нет. Например, «через 3 дня после покупки» с подсказкой, как
          подключить второе устройство.
        </p>
      ) : (
        journey.rules.map((r) => {
          const m = journey.messages.find((x) => x.rule_id === r.id);
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => {
                haptic('light');
                onOpen({ mode: 'edit', rule: r });
              }}
              className="flex w-full items-center gap-3 surface-inset rounded-[14px] p-3.5 text-left active:scale-[0.99] transition-transform"
            >
              <Mail className={cn('w-4 h-4 shrink-0', r.enabled ? 'text-primary-light' : 'text-dim')} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{r.name}</span>
                <span className="block text-[11.5px] text-muted-foreground">{r.summary}</span>
                <span className="block text-[11px] text-dim mt-0.5 tabular-nums">
                  {r.enabled
                    ? `${m?.sent ?? 0} ушло · ${m?.buyers ?? 0} купили за ${journey.attribution_hours} ч`
                    : 'Выключено'}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 shrink-0 text-dim" />
            </button>
          );
        })
      )}
      <button
        type="button"
        disabled={full}
        onClick={() => onOpen({ mode: 'new' })}
        className="flex w-full min-h-12 items-center justify-center gap-2 rounded-[14px] border border-dashed border-white/20 text-[13.5px] font-bold disabled:opacity-40"
      >
        <Plus className="w-4 h-4" />
        {full ? `Уже ${journey.limits.max_rules} из ${journey.limits.max_rules}` : 'Новое сообщение'}
      </button>
    </div>
  );
}

function Segment<T extends string>({
  value,
  onChange,
  items,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  items: ReadonlyArray<{ key: T; label: string }>;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-[11px] surface-inset p-0.5">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="radio"
          aria-checked={value === it.key}
          onClick={() => onChange(it.key)}
          className={cn(
            'min-h-9 rounded-[9px] px-3 text-[12px] font-semibold',
            value === it.key ? 'bg-white/[0.12] text-foreground' : 'text-muted-foreground',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function Editor({
  botId,
  journey,
  target,
  onDone,
}: {
  botId: string;
  journey: Journey;
  target: Target;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const rule = target.mode === 'edit' ? target.rule : null;
  const first = journey.scenarios[0];
  const initial: WarmupRuleInput = rule
    ? { ...rule }
    : {
        name: first?.label ?? 'Своё сообщение',
        scenario: first?.key ?? '',
        days: first?.default_days ?? 3,
        filters: {},
        repeat_days: null,
        text: '',
        buttons: [],
        offer_percent: null,
        enabled: true,
      };
  const [form, setForm] = useState<WarmupRuleInput>(initial);
  // Имя и дни следуют за сценарием, пока их не трогали руками.
  const [touched, setTouched] = useState({ name: !!rule, days: !!rule });
  const [daysText, setDaysText] = useState(String(initial.days));
  const [repeatText, setRepeatText] = useState(String(initial.repeat_days ?? journey.limits.min_repeat_days));
  const [offerText, setOfferText] = useState(initial.offer_percent ? String(initial.offer_percent) : '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const scenario = journey.scenarios.find((s) => s.key === form.scenario);
  const set = <K extends keyof WarmupRuleInput>(k: K, v: WarmupRuleInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['journey', botId] });
    onDone();
  };

  const save = useMutation({
    mutationFn: () => {
      const body: WarmupRuleInput = {
        ...form,
        days: Number(daysText),
        repeat_days: form.repeat_days === null ? null : Number(repeatText),
        offer_percent: offerText.trim() ? Number(offerText) : null,
      };
      return rule ? api.updateWarmupRule(botId, rule.id, body) : api.createWarmupRule(botId, body);
    },
    onSuccess: () => {
      haptic('success');
      toast.success(rule ? 'Сохранено' : 'Создано: начнёт уходить с ближайшей проверки');
      done();
    },
    onError: (e) => {
      haptic('error');
      toast.error(errText(e, 'Не сохранилось'));
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteWarmupRule(botId, rule!.id),
    onSuccess: () => {
      haptic('success');
      toast.success('Удалено');
      done();
    },
    onError: (e) => toast.error(errText(e, 'Не удалилось')),
  });
  const busy = save.isPending || remove.isPending;
  const maxLen = form.media_url ? journey.limits.caption_max : journey.limits.text_max;

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-1 pt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={onDone}
          className="w-9 h-9 rounded-[12px] surface-inset flex items-center justify-center shrink-0 active:scale-95 transition-transform"
          aria-label="Назад"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </button>
        <h2 className="font-display text-lg font-semibold tracking-tight">
          {rule ? 'Автосообщение' : 'Новое сообщение'}
        </h2>
      </div>

      <label className="block space-y-1.5">
        <span className="text-[12px] font-bold text-dim">Название (видите только вы)</span>
        <input
          value={form.name}
          maxLength={64}
          onChange={(e) => {
            set('name', e.target.value);
            setTouched((t) => ({ ...t, name: true }));
          }}
          className="w-full surface-inset rounded-[13px] px-3.5 py-3 text-[14px] outline-none"
        />
      </label>

      <div className="space-y-1.5">
        <span className="text-[12px] font-bold text-dim">Когда отправить</span>
        <div className="space-y-1.5" role="radiogroup" aria-label="Сценарий">
          {journey.scenarios.map((s) => {
            const on = s.key === form.scenario;
            return (
              <button
                key={s.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => {
                  set('scenario', s.key);
                  if (!touched.name) set('name', s.label);
                  if (!touched.days) setDaysText(String(s.default_days));
                  if (!s.per_subscription) set('buttons', form.buttons.filter((b) => b !== 'renew'));
                }}
                className={cn(
                  'block w-full surface-inset rounded-[13px] px-3.5 py-2.5 text-left',
                  on && 'ring-1 ring-white/30',
                )}
              >
                <span className="block text-[13.5px] font-bold">{s.label}</span>
                <span className="block text-[11.5px] text-muted-foreground">
                  {s.hint.replace('{n}', on ? daysText || 'N' : 'N')}
                </span>
              </button>
            );
          })}
        </div>
        <label className="flex items-center gap-2 pt-1 text-[13px]">
          Дней:
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={journey.limits.max_days}
            value={daysText}
            onChange={(e) => {
              setDaysText(e.target.value);
              setTouched((t) => ({ ...t, days: true }));
            }}
            className="w-20 surface-inset rounded-[11px] px-3 py-2 text-[14px] outline-none"
          />
        </label>
      </div>

      <div className="space-y-2">
        <span className="text-[12px] font-bold text-dim">Повтор</span>
        <div className="flex flex-wrap items-center gap-2">
          <Segment
            label="Повтор"
            value={form.repeat_days === null ? 'once' : 'repeat'}
            onChange={(v) => set('repeat_days', v === 'once' ? null : Number(repeatText))}
            items={[
              { key: 'once', label: 'Один раз' },
              { key: 'repeat', label: 'Повторять' },
            ]}
          />
          {form.repeat_days !== null && (
            <label className="flex items-center gap-1.5 text-[13px]">
              каждые
              <input
                type="number"
                inputMode="numeric"
                min={journey.limits.min_repeat_days}
                value={repeatText}
                onChange={(e) => setRepeatText(e.target.value)}
                className="w-16 surface-inset rounded-[11px] px-2.5 py-2 text-[14px] outline-none"
              />
              дн.
            </label>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <span className="text-[12px] font-bold text-dim">Кому</span>
        {FILTERS.map((f) => (
          <div key={f.key} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[13px]">{f.label}</span>
            <Segment
              label={f.label}
              value={f.key in form.filters ? (form.filters[f.key] ? 'yes' : 'no') : 'any'}
              onChange={(v) => {
                const next = { ...form.filters };
                if (v === 'any') delete next[f.key];
                else next[f.key] = v === 'yes';
                set('filters', next);
              }}
              items={[
                { key: 'any', label: 'Неважно' },
                { key: 'yes', label: 'Да' },
                { key: 'no', label: 'Нет' },
              ]}
            />
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        <span className="text-[12px] font-bold text-dim">Сообщение</span>
        <TelegramEditor value={initial.text} onChange={(html) => set('text', html)} maxLen={maxLen} />
      </div>

      <div className="space-y-1.5">
        <span className="text-[12px] font-bold text-dim">Кнопки (до {journey.limits.max_buttons})</span>
        <div className="flex flex-wrap gap-1.5">
          {journey.buttons
            .filter((b) => b.key !== 'renew' || scenario?.per_subscription)
            .map((b) => {
              const on = form.buttons.includes(b.key);
              return (
                <button
                  key={b.key}
                  type="button"
                  aria-pressed={on}
                  disabled={!on && form.buttons.length >= journey.limits.max_buttons}
                  onClick={() =>
                    set('buttons', on ? form.buttons.filter((x) => x !== b.key) : [...form.buttons, b.key])
                  }
                  className={cn(
                    'min-h-9 rounded-full px-3 text-[12px] font-semibold surface-inset disabled:opacity-40',
                    on && 'ring-1 ring-white/30 text-primary-light',
                  )}
                >
                  {b.label}
                </button>
              );
            })}
        </div>
      </div>

      <label className="flex items-center gap-2 text-[13px]">
        Скидка, %:
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={journey.limits.offer_max}
          value={offerText}
          placeholder="нет"
          onChange={(e) => setOfferText(e.target.value)}
          className="w-20 surface-inset rounded-[11px] px-3 py-2 text-[14px] outline-none"
        />
      </label>
      {form.media_url ? (
        <p className="text-[11.5px] text-dim">Картинка есть: поменять её можно на сайте.</p>
      ) : (
        <p className="text-[11.5px] text-dim">Картинку можно добавить на сайте, раздел «Путь клиента».</p>
      )}

      <label className="flex items-center justify-between gap-3 surface-inset rounded-[13px] px-3.5 py-3">
        <span className="text-sm font-bold">{form.enabled ? 'Включено' : 'Выключено'}</span>
        <Switch checked={form.enabled} onCheckedChange={(v: boolean) => set('enabled', v)} />
      </label>

      <button
        type="button"
        disabled={busy || !form.name.trim() || !form.text.trim()}
        onClick={() => save.mutate()}
        className="w-full min-h-12 rounded-[13px] bg-primary text-primary-foreground text-[14px] font-bold disabled:opacity-40 active:scale-[0.99] transition-transform"
      >
        {save.isPending ? 'Сохраняем…' : rule ? 'Сохранить' : 'Создать'}
      </button>
      {rule &&
        (confirmDelete ? (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => remove.mutate()}
              className="flex-1 min-h-11 rounded-[13px] bg-danger text-white text-[13.5px] font-bold"
            >
              Удалить
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="flex-1 min-h-11 rounded-[13px] surface-inset text-[13.5px] font-bold"
            >
              Отмена
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="flex w-full min-h-11 items-center justify-center gap-2 text-[13px] text-danger"
          >
            <Trash2 className="w-4 h-4" /> Удалить сообщение
          </button>
        ))}
    </div>
  );
}
